import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List as MuiList,
  ListItem as MuiListItem,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText
} from '@mui/material';
import {
  Compare as CompareIcon,
  TrendingUp as TrendingIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Visibility as VisibilityIcon,
  PhoneAndroid as MobileIcon,
  ShoppingCart as EcommerceIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { websiteAnalyzer } from '../utils/websiteAnalyzer';

const CompetitiveAnalysis = ({ business, websiteAnalysis }) => {
  const [competitors, setCompetitors] = useState([]);
  const [newCompetitor, setNewCompetitor] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);

  if (!business || !websiteAnalysis) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" color="text.secondary">
          Analysez d'abord un site web pour accéder à l'analyse concurrentielle
        </Typography>
      </Box>
    );
  }

  const addCompetitor = () => {
    if (newCompetitor.trim() && !competitors.includes(newCompetitor.trim())) {
      setCompetitors([...competitors, newCompetitor.trim()]);
      setNewCompetitor('');
    }
  };

  const removeCompetitor = (index) => {
    setCompetitors(competitors.filter((_, i) => i !== index));
  };

  const analyzeCompetitors = async () => {
    if (competitors.length === 0) return;

    setIsAnalyzing(true);
    const results = {
      target: {
        name: business.name,
        website: business.website,
        analysis: websiteAnalysis
      },
      competitors: []
    };

    try {
      for (const competitorUrl of competitors) {
        const analysis = await websiteAnalyzer.analyzeWebsite(competitorUrl);
        results.competitors.push({
          name: competitorUrl.replace(/^https?:\/\//, '').replace(/\/$/, ''),
          website: competitorUrl,
          analysis
        });
      }
      setAnalysisResults(results);
    } catch (error) {
      console.error('Erreur lors de l\'analyse concurrentielle:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getCompetitiveAdvantage = (target, competitor) => {
    const advantages = [];
    const disadvantages = [];

    // Performance
    if (target.analysis.performance.performanceScore > competitor.analysis.performance.performanceScore) {
      advantages.push(`Performance +${target.analysis.performance.performanceScore - competitor.analysis.performance.performanceScore} points`);
    } else {
      disadvantages.push(`Performance -${competitor.analysis.performance.performanceScore - target.analysis.performance.performanceScore} points`);
    }

    // SEO
    if (target.analysis.seo.seoScore > competitor.analysis.seo.seoScore) {
      advantages.push(`SEO +${target.analysis.seo.seoScore - competitor.analysis.seo.seoScore} points`);
    } else {
      disadvantages.push(`SEO -${competitor.analysis.seo.seoScore - target.analysis.seo.seoScore} points`);
    }

    // Mobile
    if (target.analysis.mobile.mobileScore > competitor.analysis.mobile.mobileScore) {
      advantages.push(`Mobile +${target.analysis.mobile.mobileScore - competitor.analysis.mobile.mobileScore} points`);
    } else {
      disadvantages.push(`Mobile -${competitor.analysis.mobile.mobileScore - target.analysis.mobile.mobileScore} points`);
    }

    return { advantages, disadvantages };
  };

  const generateCompetitiveReport = () => {
    if (!analysisResults) return null;

    const report = {
      summary: {
        targetScore: analysisResults.target.analysis.overallScore,
        averageCompetitorScore: analysisResults.competitors.reduce((sum, comp) => sum + comp.analysis.overallScore, 0) / analysisResults.competitors.length,
        position: analysisResults.target.analysis.overallScore > (analysisResults.competitors.reduce((sum, comp) => sum + comp.analysis.overallScore, 0) / analysisResults.competitors.length) ? 'leader' : 'suiveur'
      },
      opportunities: [],
      threats: [],
      recommendations: []
    };

    // Analyser les opportunités et menaces
    analysisResults.competitors.forEach(competitor => {
      const comparison = getCompetitiveAdvantage(analysisResults.target, competitor);
      
      if (comparison.advantages.length > 0) {
        report.opportunities.push({
          competitor: competitor.name,
          advantages: comparison.advantages
        });
      }
      
      if (comparison.disadvantages.length > 0) {
        report.threats.push({
          competitor: competitor.name,
          disadvantages: comparison.disadvantages
        });
      }
    });

    // Générer des recommandations
    if (report.summary.position === 'suiveur') {
      report.recommendations.push('Prioriser l\'amélioration des performances pour rattraper la concurrence');
      report.recommendations.push('Investir dans l\'optimisation SEO pour améliorer la visibilité');
      report.recommendations.push('Optimiser l\'expérience mobile pour capturer plus de trafic');
    } else {
      report.recommendations.push('Maintenir l\'avantage concurrentiel en continuant les optimisations');
      report.recommendations.push('Surveiller les améliorations de la concurrence');
      report.recommendations.push('Investir dans l\'innovation pour creuser l\'écart');
    }

    return report;
  };

  const competitiveReport = generateCompetitiveReport();

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CompareIcon color="primary" />
        Analyse Concurrentielle
      </Typography>

      <Typography variant="body1" sx={{ mb: 3 }}>
        Comparez votre site web avec vos concurrents pour identifier vos avantages et opportunités d'amélioration.
      </Typography>

      {/* Ajout de concurrents */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Ajouter des Concurrents
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="URL du site concurrent"
              value={newCompetitor}
              onChange={(e) => setNewCompetitor(e.target.value)}
              placeholder="https://www.concurrent.com"
              onKeyPress={(e) => e.key === 'Enter' && addCompetitor()}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addCompetitor}
              disabled={!newCompetitor.trim()}
            >
              Ajouter
            </Button>
          </Box>

          {competitors.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Concurrents à analyser :
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {competitors.map((competitor, index) => (
                  <Chip
                    key={index}
                    label={competitor}
                    onDelete={() => removeCompetitor(index)}
                    deleteIcon={<DeleteIcon />}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Bouton d'analyse */}
      {competitors.length > 0 && (
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<CompareIcon />}
            onClick={analyzeCompetitors}
            disabled={isAnalyzing}
          >
            {isAnalyzing ? 'Analyse en cours...' : 'Lancer l\'Analyse Concurrentielle'}
          </Button>
        </Box>
      )}

      {/* Résultats de l'analyse */}
      {analysisResults && competitiveReport && (
        <Box>
          {/* Résumé */}
          <Card sx={{ mb: 3, bgcolor: competitiveReport.summary.position === 'leader' ? 'success.light' : 'warning.light' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 Position Concurrentielle
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography variant="h4" color="primary">
                    {competitiveReport.summary.targetScore}/100
                  </Typography>
                  <Typography variant="body2">Votre Score</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="h4" color="secondary">
                    {Math.round(competitiveReport.summary.averageCompetitorScore)}/100
                  </Typography>
                  <Typography variant="body2">Moyenne Concurrents</Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Chip
                    label={competitiveReport.summary.position === 'leader' ? 'Leader' : 'Suiveur'}
                    color={competitiveReport.summary.position === 'leader' ? 'success' : 'warning'}
                    size="large"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Tableau comparatif */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📈 Comparaison Détaillée
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Site</TableCell>
                      <TableCell>Score Global</TableCell>
                      <TableCell>Performance</TableCell>
                      <TableCell>SEO</TableCell>
                      <TableCell>Mobile</TableCell>
                      <TableCell>Sécurité</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ bgcolor: 'primary.light' }}>
                      <TableCell>
                        <strong>{analysisResults.target.name}</strong>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${analysisResults.target.analysis.overallScore}/100`}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{analysisResults.target.analysis.performance.performanceScore}</TableCell>
                      <TableCell>{analysisResults.target.analysis.seo.seoScore}</TableCell>
                      <TableCell>{analysisResults.target.analysis.mobile.mobileScore}</TableCell>
                      <TableCell>{analysisResults.target.analysis.security.securityScore}</TableCell>
                      <TableCell>
                        <Chip label="Votre site" color="primary" size="small" />
                      </TableCell>
                    </TableRow>
                    {analysisResults.competitors.map((competitor, index) => (
                      <TableRow key={index}>
                        <TableCell>{competitor.name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={`${competitor.analysis.overallScore}/100`}
                            color={competitor.analysis.overallScore > analysisResults.target.analysis.overallScore ? 'error' : 'success'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{competitor.analysis.performance.performanceScore}</TableCell>
                        <TableCell>{competitor.analysis.seo.seoScore}</TableCell>
                        <TableCell>{competitor.analysis.mobile.mobileScore}</TableCell>
                        <TableCell>{competitor.analysis.security.securityScore}</TableCell>
                        <TableCell>
                          <Tooltip title="Voir détails">
                            <IconButton size="small">
                              <CompareIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* Opportunités et menaces */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ bgcolor: 'success.light' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    ✅ Vos Avantages
                  </Typography>
                  {competitiveReport.opportunities.length > 0 ? (
                    competitiveReport.opportunities.map((opp, index) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="success.dark">
                          vs {opp.competitor}
                        </Typography>
                        {opp.advantages.map((adv, i) => (
                          <Typography key={i} variant="body2" sx={{ ml: 2 }}>
                            • {adv}
                          </Typography>
                        ))}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucun avantage significatif identifié
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={{ bgcolor: 'error.light' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    ⚠️ Menaces Concurrentielles
                  </Typography>
                  {competitiveReport.threats.length > 0 ? (
                    competitiveReport.threats.map((threat, index) => (
                      <Box key={index} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="error.dark">
                          vs {threat.competitor}
                        </Typography>
                        {threat.disadvantages.map((dis, i) => (
                          <Typography key={i} variant="body2" sx={{ ml: 2 }}>
                            • {dis}
                          </Typography>
                        ))}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Aucune menace significative identifiée
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Recommandations */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🎯 Recommandations Stratégiques
              </Typography>
              <MuiList>
                {competitiveReport.recommendations.map((rec, index) => (
                  <MuiListItem key={index}>
                    <MuiListItemIcon>
                      <CheckIcon color="primary" />
                    </MuiListItemIcon>
                    <MuiListItemText primary={rec} />
                  </MuiListItem>
                ))}
              </MuiList>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default CompetitiveAnalysis;